
import React from 'react';
import { QuoteData, FeeType, BillingCadence } from '../types';

interface QuotePreviewProps {
  quote: QuoteData;
}

export const QuotePreview: React.FC<QuotePreviewProps> = ({ quote }) => {
  const getMultiplier = (itemType: FeeType, cadence: BillingCadence): number => {
    if (itemType === FeeType.FIXED) return 1;
    
    if (itemType === FeeType.MONTHLY) {
      if (cadence === BillingCadence.MONTHLY) return 1;
      if (cadence === BillingCadence.QUARTERLY) return 3;
      if (cadence === BillingCadence.YEARLY) return 12;
    }
    
    if (itemType === FeeType.YEARLY) {
      if (cadence === BillingCadence.MONTHLY) return 1/12;
      if (cadence === BillingCadence.QUARTERLY) return 1/4;
      if (cadence === BillingCadence.YEARLY) return 1;
    }
    
    return 1;
  };

  const calculateTotals = () => {
    let setup = 0;
    let recurringTotal = 0;

    quote.items.forEach(item => {
      const multiplier = getMultiplier(item.type, quote.billingCadence);
      const lineTotal = item.price * item.quantity * multiplier;
      
      if (item.type === FeeType.FIXED) {
        setup += lineTotal;
      } else {
        recurringTotal += lineTotal;
      }
    });

    const subtotal = setup + recurringTotal;
    const moms = subtotal * quote.momsRate;
    const totalInklMoms = subtotal + moms;

    return { setup, recurringTotal, subtotal, moms, totalInklMoms };
  };

  const totals = calculateTotals();
  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('sv-SE', { 
      style: 'currency', 
      currency: 'SEK',
      minimumFractionDigits: 2 
    }).format(val);

  const getUnitLabel = (type: FeeType) => {
    switch(type) {
      case FeeType.MONTHLY: return '/mån';
      case FeeType.YEARLY: return '/år';
      default: return ' st';
    }
  };

  return (
    <div id="quote-preview" className="bg-white p-12 shadow-2xl rounded-sm max-w-[800px] mx-auto text-slate-800 border border-slate-100 min-h-[1100px] flex flex-col font-sans transition-all duration-300">
      {/* Header */}
      <div className="flex justify-between items-start mb-12">
        <div className="flex flex-col items-start">
          <img 
            src="https://frostpil.pages.dev/assets/frostpil-logo-EFFG_rCZ.png" 
            alt="Frostpil" 
            className="h-10 w-auto object-contain mb-3"
          />
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold">Affärsoffert</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-lg text-slate-900">{quote.client.companyName || 'Kund AB'}</p>
          <p className="text-slate-500 text-sm whitespace-pre-line leading-relaxed">{quote.client.address || 'Adressgatan 1\n123 45 Stad'}</p>
          <p className="text-slate-500 text-sm mt-3 font-medium">Att: {quote.client.contactPerson || '-'}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8 mb-10 py-4 border-y border-slate-100">
        <div>
          <p className="text-[10px] uppercase text-slate-400 font-bold mb-1 tracking-wider">Offertnummer</p>
          <p className="font-semibold text-slate-700 text-sm">#{quote.id}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase text-slate-400 font-bold mb-1 tracking-wider">Vår referens</p>
          <p className="font-semibold text-slate-700 text-sm">{quote.sender.name}</p>
          <p className="text-[10px] text-slate-400">{quote.sender.phone}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase text-slate-400 font-bold mb-1 tracking-wider">Giltighet</p>
          <p className="font-semibold text-slate-700 text-sm">{quote.date} <span className="text-slate-300 mx-1">|</span> {quote.validUntil}</p>
        </div>
      </div>

      {/* Intro Message */}
      <div className="mb-10 text-slate-600 leading-relaxed italic border-l-2 border-blue-500 pl-6 py-1 text-sm">
        {quote.notes || 'Tack för visat intresse. Nedan följer vår offert enligt diskussion gällande era digitala tjänster.'}
      </div>

      {/* Line Items */}
      <div className="flex-grow">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              <th className="pb-4">Beskrivning</th>
              <th className="pb-4 w-16 text-center">Antal</th>
              <th className="pb-4 w-28 text-right">Pris</th>
              <th className="pb-4 w-36 text-right">Summa ({quote.billingCadence.toLowerCase()})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {quote.items.map((item) => {
              const multiplier = getMultiplier(item.type, quote.billingCadence);
              const lineSum = item.price * item.quantity * multiplier;
              
              return (
                <tr key={item.id} className="align-top">
                  <td className="py-4 pr-4">
                    <p className="font-bold text-slate-900 text-sm">{item.name}</p>
                    {item.description && <p className="text-xs text-slate-500 leading-relaxed mt-1">{item.description}</p>}
                    <span className="inline-block mt-2 text-[8px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-bold tracking-wider">
                      {item.type === FeeType.FIXED ? 'Engångs' : item.type === FeeType.MONTHLY ? 'Månadsvis' : 'Årsvis'}
                    </span>
                  </td>
                  <td className="py-4 text-center text-slate-600 font-medium text-sm">{item.quantity}</td>
                  <td className="py-4 text-right text-slate-500 tabular-nums text-sm">
                    {formatCurrency(item.price)}
                    <span className="text-[9px] ml-0.5 opacity-60 font-normal">{getUnitLabel(item.type)}</span>
                  </td>
                  <td className="py-4 text-right tabular-nums">
                    <div className="font-bold text-slate-900 text-sm">{formatCurrency(lineSum)}</div>
                    {multiplier !== 1 && (
                      <div className="text-[9px] text-slate-400 font-medium italic">
                        {multiplier === 12 ? 'för 12 månader' : multiplier === 3 ? 'för 3 månader' : multiplier < 1 ? 'per månad' : ''}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      <div className="mt-8 pt-8 border-t border-slate-100">
        <div className="flex flex-col md:flex-row justify-between gap-12">
          {/* Terms Box */}
          <div className="flex-1">
            <div className="bg-[#f8fafc] p-6 rounded-xl border border-slate-100">
              <h4 className="text-[10px] uppercase font-bold text-blue-900/40 mb-2 tracking-widest">Betalningsvillkor</h4>
              <p className="text-xs text-slate-600 leading-relaxed mb-3">
                30 dagar netto efter fakturadatum. Löpande kostnader faktureras <span className="font-bold text-slate-900">{quote.billingCadence.toLowerCase()}</span> i förskott.
              </p>
              <div className="h-px bg-slate-200 mb-3"></div>
              <p className="text-[10px] text-slate-400 leading-relaxed">
                Uppdraget utförs av Frostpil. Fakturering sker via <span className="font-medium text-slate-600">Frilans Finans (Org.nr: 556552-3415)</span> eller <span className="font-medium text-slate-600">INVOZIO LV SIA (Org.nr 502085-4062)</span> som innehar F-skattsedel.
              </p>
            </div>
          </div>

          {/* Totals Table */}
          <div className="w-full md:w-80 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-400">Total exkl. moms:</span>
              <span className="text-slate-700 font-medium tabular-nums">{formatCurrency(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-400">Moms (25%):</span>
              <span className="text-slate-700 font-medium tabular-nums">{formatCurrency(totals.moms)}</span>
            </div>
            
            <div className="pt-3 mt-3 border-t-2 border-slate-100">
              <div className="flex justify-between items-end mb-1">
                <span className="font-bold text-blue-900 text-xs uppercase tracking-tight">ATT BETALA INKL. MOMS:</span>
                <span className="font-bold text-xl text-blue-900 leading-none tabular-nums">
                  {formatCurrency(totals.totalInklMoms)} 
                </span>
              </div>
              <p className="text-right text-[9px] text-slate-400 italic">Avser första fakturaperioden inkl. startkostnader</p>
            </div>
          </div>
        </div>
      </div>

      {/* Acceptance Section */}
      <div className="mt-12 pt-8 border-t border-slate-100 grid grid-cols-2 gap-12">
        <div className="space-y-4">
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Acceptans</p>
          <div className="text-xs text-slate-500 leading-relaxed">
            Genom att signera nedan eller skriftligen acceptera denna offert via e-post godkänns ovanstående villkor och priser.
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <div className="border-b border-slate-200 h-10 flex items-end">
            <span className="text-[8px] uppercase text-slate-300 font-bold mb-1">Ort & Datum</span>
          </div>
          <div className="border-b border-slate-200 h-10 flex items-end">
            <span className="text-[8px] uppercase text-slate-300 font-bold mb-1">Namnteckning & Namnförtydligande</span>
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="mt-12 text-center">
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6"></div>
        <p className="text-[9px] text-slate-400 uppercase tracking-widest font-medium">
          Frostpil <span className="mx-2">|</span> {quote.sender.email} <span className="mx-2">|</span> www.frostpil.se
        </p>
      </div>
    </div>
  );
};
